import React from 'react';

/** Rende una stringa con "\n" come <br/>. */
export function renderMultiline(text: string): React.ReactNode {
  const parts = text.split('\n');
  return parts.map((p, i) => (
    <React.Fragment key={i}>
      {p}
      {i < parts.length - 1 && <br />}
    </React.Fragment>
  ));
}

/** Formatta un numero come valuta EUR italiana. */
export function euro(n: number): string {
  return Math.round(n).toLocaleString('it-IT') + '€';
}

/**
 * Rende formattazione inline: **grassetto**, [testo](url) e auto-link delle email.
 */
function renderInline(text: string, keyPrefix: string): React.ReactNode[] {
  // Divide su **grassetto** e [testo](url), preservando il testo intorno.
  const tokens = text.split(/(\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g);
  const out: React.ReactNode[] = [];
  tokens.forEach((tok, i) => {
    if (!tok) return;
    const bold = tok.match(/^\*\*([^*]+)\*\*$/);
    const link = tok.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (bold) {
      out.push(<strong key={`${keyPrefix}-${i}`}>{bold[1]}</strong>);
    } else if (link) {
      out.push(<a key={`${keyPrefix}-${i}`} href={link[2]}>{link[1]}</a>);
    } else {
      // Auto-link delle email nel testo semplice.
      const parts = tok.split(/([\w.+-]+@[\w-]+\.[\w.-]+)/g);
      parts.forEach((p, j) => {
        if (!p) return;
        if (/^[\w.+-]+@[\w-]+\.[\w.-]+$/.test(p)) {
          out.push(<a key={`${keyPrefix}-${i}-${j}`} href={`mailto:${p}`}>{p}</a>);
        } else {
          out.push(<React.Fragment key={`${keyPrefix}-${i}-${j}`}>{p}</React.Fragment>);
        }
      });
    }
  });
  return out;
}

/** Rende solo la formattazione inline (**grassetto**, [testo](url), email) senza blocchi. */
export function renderInlineMd(text: string): React.ReactNode {
  return renderInline(text, 'i');
}

/**
 * Renderer di testo formattato con una sintassi minima (Markdown ridotto), usato per i testi lunghi
 * modificabili da dashboard (es. pagine legali):
 *   - righe che iniziano con "## " → titolo di sezione
 *   - righe che iniziano con "- " → elenco puntato
 *   - blocchi separati da riga vuota → paragrafi
 *   - inline: **grassetto**, [testo](url), email auto-linkate
 */
export function renderRich(text: string): React.ReactNode {
  const blocks = text.replace(/\r\n/g, '\n').split(/\n\s*\n/);
  return blocks.map((block, bi) => {
    const lines = block.split('\n').filter((l) => l.trim() !== '');
    if (lines.length === 0) return null;

    // Elenco puntato: tutte le righe iniziano con "- "
    if (lines.every((l) => l.trim().startsWith('- '))) {
      return (
        <ul key={bi}>
          {lines.map((l, li) => <li key={li}>{renderInline(l.trim().slice(2), `${bi}-${li}`)}</li>)}
        </ul>
      );
    }

    // Titolo di sezione
    if (lines[0].startsWith('## ')) {
      return <h2 key={bi}>{renderInline(lines[0].slice(3), `${bi}-h`)}</h2>;
    }

    // Paragrafo (le righe interne diventano <br/>)
    return (
      <p key={bi}>
        {lines.map((l, li) => (
          <React.Fragment key={li}>
            {renderInline(l, `${bi}-${li}`)}
            {li < lines.length - 1 && <br />}
          </React.Fragment>
        ))}
      </p>
    );
  });
}
